import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { SuperAdminPageHelp } from '@/components/SuperAdmin/SuperAdminPageHelp';
import { ProjectSearchSelect } from '@/components/ProjectSearchSelect';
import { AttributeTimeseriesModal } from '@/components/Influx/AttributeTimeseriesModal';
import { useAuthStore } from '@/store/authStore';
import { useTheme } from '@/hooks/useTheme';
import { areaService } from '@/services/areaService';
import { projectService } from '@/services/projectService';
import { boxService } from '@/services/boxService';
import { deviceService } from '@/services/deviceService';
import { superAssociationsApi } from '@/api/superAssociationsApi';
import { httpPost } from '@/api/httpClient';
import type { Area, Box } from '@/types';

type Tab = 'summary' | 'apartments' | 'apartmentDeviceCategories' | 'rooms';

function getErrorMessage(err: unknown): string {
  if (!err) return 'Erreur';
  if (typeof err === 'object' && err !== null && 'body' in err) {
    const body = (err as { body?: unknown }).body;
    if (typeof body === 'object' && body !== null && 'message' in body) {
      const msg = (body as { message?: unknown }).message;
      if (typeof msg === 'string' && msg) return msg;
    }
  }
  if (err instanceof Error) return err.message || 'Erreur';
  return 'Erreur';
}

function areaTypeCode(a: Area | undefined | null): string {
  const t: any = (a as any)?.type;
  return (t?.code as string) || '';
}

function parentId(a: Area): number | null {
  const p: any = (a as any)?.parent;
  if (!p) return null;
  if (typeof p === 'object' && typeof p.id === 'number') return p.id;
  return null;
}

function resolveAncestorIdByTypeCode(area: Area, targetTypeCode: string, areasById: Map<number, Area>): number | null {
  // Walk up the parent chain to find the first ancestor matching target type.
  // Typical hierarchy: BUILDING -> FLOOR -> APPARTEMENT -> ROOM
  let cur: Area | undefined = area;
  for (let i = 0; i < 12; i++) {
    if (!cur) return null;
    if (areaTypeCode(cur) === targetTypeCode) return cur.id;
    const pid = parentId(cur);
    if (!pid) return null;
    cur = areasById.get(pid);
  }
  return null;
}

async function mapWithConcurrency<T, R>(items: T[], concurrency: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let idx = 0;

  async function worker() {
    while (true) {
      const cur = idx++;
      if (cur >= items.length) return;
      results[cur] = await fn(items[cur]);
    }
  }

  const workers = Array.from({ length: Math.max(1, Math.min(concurrency, items.length)) }, () => worker());
  await Promise.all(workers);
  return results;
}

type SuperAssociationsPageProps = {
  forcedProjectId?: number;
};

export function SuperAssociationsPage({ forcedProjectId }: SuperAssociationsPageProps = {}) {
  const { user } = useAuthStore();
  const { isDark, toggleTheme } = useTheme();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<Tab>('summary');
  const [selectedProjectIri, setSelectedProjectIri] = useState<string>('');

  const [selectedBuildingId, setSelectedBuildingId] = useState<number>(0);
  const [selectedApartmentId, setSelectedApartmentId] = useState<number>(0);
  const [selectedRoomId, setSelectedRoomId] = useState<number>(0);

  const [boxSearch, setBoxSearch] = useState<string>('');
  const [deviceSearch, setDeviceSearch] = useState<string>('');

  const [isAttrModalOpen, setIsAttrModalOpen] = useState<boolean>(false);
  const [selectedAttributeForModal, setSelectedAttributeForModal] = useState<{
    uuid: string;
    name: string;
    roomName?: string;
    unit?: string | null;
    attributeTypeCode?: string | null;
  } | null>(null);

  const platformProjectId = useMemo(() => {
    if (typeof forcedProjectId === 'number' && forcedProjectId > 0) return forcedProjectId;
    if (!selectedProjectIri) return null;
    const n = parseInt(selectedProjectIri.replace('/api/projects/', ''), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [selectedProjectIri, forcedProjectId]);

  const forcedProjectQuery = useQuery({
    queryKey: ['superAssociationsForcedProject', forcedProjectId],
    enabled: typeof forcedProjectId === 'number' && forcedProjectId > 0,
    queryFn: async () => {
      return await projectService.getProject(forcedProjectId as number);
    },
    staleTime: 60_000,
  });

  useEffect(() => {
    if (typeof forcedProjectId !== 'number' || forcedProjectId <= 0) return;
    setActiveTab('summary');
    setSelectedBuildingId(0);
    setSelectedApartmentId(0);
    setSelectedRoomId(0);
    setBoxSearch('');
    setDeviceSearch('');
  }, [forcedProjectId]);

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const areasQuery = useQuery<Area[]>({
    queryKey: ['superAssociationsAreas', platformProjectId],
    enabled: !!platformProjectId,
    queryFn: async () => {
      if (!platformProjectId) return [];
      // Uses optimized admin endpoint with type + parent + boxes/devices lists
      return areaService.getAdminAreasAssociationLight(platformProjectId);
    },
  });

  const boxesQuery = useQuery<Box[]>({
    queryKey: ['superAssociationsBoxes', platformProjectId],
    enabled: !!platformProjectId,
    queryFn: async () => {
      if (!platformProjectId) return [];
      const res = await boxService.getBoxesByProject(platformProjectId, undefined, 1, 10000);
      return res.data ?? [];
    },
  });

  const devicesQuery = useQuery({
    queryKey: ['superAssociationsDevices', platformProjectId],
    enabled: !!platformProjectId,
    queryFn: async () => {
      if (!platformProjectId) throw new Error('Missing project');
      return superAssociationsApi.getDevices(platformProjectId);
    },
  });

  const roomsWithRemovedDevicesQuery = useQuery({
    queryKey: ['superAssociationsRoomsWithRemovedDevices', platformProjectId],
    enabled: !!platformProjectId,
    queryFn: async () => {
      if (!platformProjectId) throw new Error('Missing project');
      return superAssociationsApi.getRoomsWithRemovedDevices(platformProjectId);
    },
  });

  const roomDevicesQuery = useQuery({
    queryKey: ['superAssociationsRoomDevices', platformProjectId, selectedRoomId],
    enabled: !!platformProjectId && selectedRoomId > 0,
    queryFn: async () => {
      if (!platformProjectId || !selectedRoomId) throw new Error('Missing project/room');
      return superAssociationsApi.getAreaDevices(platformProjectId, selectedRoomId);
    },
  });

  const areas = areasQuery.data ?? [];
  const boxes = boxesQuery.data ?? [];
  const devicesResp = devicesQuery.data as any;
  const devices = (devicesResp?.devices ?? []) as Array<{
    id: number;
    name: string;
    code: string;
    boxId: number;
    type: string | null;
    areaIds: number[];
  }>;

  const areasById = useMemo(() => {
    const m = new Map<number, Area>();
    areas.forEach((a) => m.set(a.id, a));
    return m;
  }, [areas]);

  const boxesById = useMemo(() => {
    const m = new Map<number, Box>();
    boxes.forEach((b) => m.set(b.id, b));
    return m;
  }, [boxes]);

  const buildings = useMemo(() => areas.filter((a) => areaTypeCode(a) === 'BUILDING'), [areas]);
  const apartments = useMemo(() => areas.filter((a) => areaTypeCode(a) === 'APPARTEMENT'), [areas]);
  const rooms = useMemo(() => areas.filter((a) => areaTypeCode(a) === 'ROOM'), [areas]);

  const apartmentsByBuilding = useMemo(() => {
    if (!selectedBuildingId) return [];
    const list = apartments.filter((a) => resolveAncestorIdByTypeCode(a, 'BUILDING', areasById) === selectedBuildingId);
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [apartments, selectedBuildingId, areasById]);

  const roomsByApartment = useMemo(() => {
    if (!selectedApartmentId) return [];
    const list = rooms.filter((a) => resolveAncestorIdByTypeCode(a, 'APPARTEMENT', areasById) === selectedApartmentId);
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [rooms, selectedApartmentId, areasById]);

  const apartmentRoomsAttributesQuery = useQuery({
    queryKey: [
      'superAssociationsApartmentRoomsAttributes',
      platformProjectId,
      selectedApartmentId,
      roomsByApartment.map((r) => r.id).join(','),
    ],
    enabled: !!platformProjectId && activeTab === 'apartmentDeviceCategories' && selectedApartmentId > 0,
    queryFn: async () => {
      if (!platformProjectId || selectedApartmentId <= 0) {
        return { rooms: [] as Array<{ id: number; name: string; attributes: any[] }> };
      }

      const roomsToLoad = roomsByApartment.map((r) => ({ id: r.id, name: r.name }));
      const loaded = await mapWithConcurrency(roomsToLoad, 5, async (room) => {
        const res = await areaService.getProjectAreaAttributes(platformProjectId, room.id);
        return {
          id: room.id,
          name: room.name,
          attributes: res.items ?? [],
        };
      });

      return { rooms: loaded };
    },
  });

  type InfluxLastBatchItem = {
    attribute_uuid: string;
    time: string | null;
    value: unknown;
    field: string | null;
    error: string | null;
  };

  const apartmentAttributesLastQuery = useQuery({
    queryKey: [
      'superAssociationsApartmentAttributesLast',
      platformProjectId,
      selectedApartmentId,
      apartmentRoomsAttributesQuery.data?.rooms
        ?.flatMap((r: any) => (r.attributes ?? []).map((a: any) => a.uuid).filter((u: any) => typeof u === 'string'))
        .sort()
        .join(',') ?? '',
    ],
    enabled: !!platformProjectId && activeTab === 'apartmentDeviceCategories' && selectedApartmentId > 0 && !!apartmentRoomsAttributesQuery.data,
    queryFn: async () => {
      const rooms = apartmentRoomsAttributesQuery.data?.rooms ?? [];
      const uuids = Array.from(
        new Set(
          rooms
            .flatMap((r: any) => (r.attributes ?? []).map((a: any) => a.uuid))
            .filter((u: any) => typeof u === 'string' && u.trim() !== '')
            .map((u: string) => u.trim())
        )
      );

      if (uuids.length === 0) {
        return { count: 0, items: [] as InfluxLastBatchItem[] };
      }

      return await httpPost<{ count: number; items: InfluxLastBatchItem[] }, { attribute_uuids: string[] }>(
        '/influx/last/batch',
        { attribute_uuids: uuids }
      );
    },
    staleTime: 0,
  });

  const lastByUuid = useMemo(() => {
    const m = new Map<string, InfluxLastBatchItem>();
    (apartmentAttributesLastQuery.data?.items ?? []).forEach((it) => {
      if (it?.attribute_uuid) m.set(it.attribute_uuid, it);
    });
    return m;
  }, [apartmentAttributesLastQuery.data]);

  const selectedApartment = useMemo(
    () => (selectedApartmentId ? areasById.get(selectedApartmentId) : null),
    [areasById, selectedApartmentId]
  );

  const activeDeviceIds = useMemo(() => new Set(devices.map((d) => d.id)), [devices]);

  const apartmentsZeroBox = useMemo(() => {
    return apartments
      .filter((a: any) => (a.boxes?.length ?? 0) === 0)
      .map((a) => ({ id: a.id, name: a.name, code: a.code }));
  }, [apartments]);

  const roomsZeroDevices = useMemo(() => {
    return rooms
      .filter((a: any) => {
        const ids = (a.devices ?? []).map((x: any) => x.id).filter((id: any) => typeof id === 'number');
        const active = ids.filter((id: number) => activeDeviceIds.has(id));
        return active.length === 0;
      })
      .map((a) => ({ id: a.id, name: a.name, code: a.code }));
  }, [rooms, activeDeviceIds]);

  const selectedApartmentBoxIds = useMemo(() => {
    if (!selectedApartment) return new Set<number>();
    const b: any = selectedApartment as any;
    const ids = (b.boxes ?? []).map((x: any) => x.id).filter((id: any) => typeof id === 'number');
    return new Set<number>(ids);
  }, [selectedApartment]);

  const selectedApartmentBoxes = useMemo(() => {
    const ids = Array.from(selectedApartmentBoxIds.values());
    const list = ids
      .map((id) => boxesById.get(id) ?? { id, name: `Box #${id}`, code: '' })
      .map((b: any) => ({ id: b.id as number, name: (b.name as string) ?? `Box #${b.id}`, code: (b.code as string) ?? '' }));
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedApartmentBoxIds, boxesById]);

  const filteredBoxes = useMemo(() => {
    const q = boxSearch.trim().toLowerCase();
    const list = boxes.slice();
    return list
      .filter((b) => (q ? `${b.name ?? ''}`.toLowerCase().includes(q) : true))
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  }, [boxes, boxSearch]);

  const filteredDevices = useMemo(() => {
    const q = deviceSearch.trim().toLowerCase();
    return devices
      .filter((d) => (q ? `${d.name}`.toLowerCase().includes(q) : true))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [devices, deviceSearch]);

  const associateBoxMutation = useMutation({
    mutationFn: async ({ boxId, areaId }: { boxId: number; areaId: number }) => boxService.associateBoxWithArea(boxId, areaId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['superAssociationsAreas', platformProjectId] });
    },
  });

  const disassociateBoxMutation = useMutation({
    mutationFn: async ({ boxId, areaId }: { boxId: number; areaId: number }) => boxService.disassociateBoxFromArea(boxId, areaId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['superAssociationsAreas', platformProjectId] });
    },
  });

  const associateDeviceMutation = useMutation({
    mutationFn: async ({ deviceId, areaId }: { deviceId: number; areaId: number }) =>
      deviceService.associateDeviceWithArea(deviceId, areaId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['superAssociationsAreas', platformProjectId] });
      await queryClient.invalidateQueries({ queryKey: ['superAssociationsDevices', platformProjectId] });
    },
  });

  const disassociateDeviceMutation = useMutation({
    mutationFn: async ({ deviceId, areaId }: { deviceId: number; areaId: number }) =>
      deviceService.disassociateDeviceFromArea(deviceId, areaId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['superAssociationsAreas', platformProjectId] });
      await queryClient.invalidateQueries({ queryKey: ['superAssociationsDevices', platformProjectId] });
      await queryClient.invalidateQueries({ queryKey: ['superAssociationsRoomDevices', platformProjectId, selectedRoomId] });
    },
  });

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: 'summary', label: 'Synthèse' },
    { id: 'apartmentDeviceCategories', label: 'Vérifier les données d\'appartements' },
    { id: 'apartments', label: 'Appartements (Boxs)' },
    { id: 'rooms', label: 'Chambres (Devices)' },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      <AdminSidebar getUserInitials={getUserInitials} />

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
            <span className="text-slate-400 dark:text-slate-600">Super utilisateur</span> / Super Associations
          </div>
          <div className="flex items-center gap-4">
            <SuperAdminPageHelp />
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
            >
              <span className="material-icons-outlined">{isDark ? 'light_mode' : 'dark_mode'}</span>
            </button>
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <span className="material-icons-outlined text-lg">person</span>
              <span>{user?.fullName || 'Admin'}</span>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Super Associations</h1>

            <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl p-5 space-y-4 mb-6">
              {typeof forcedProjectId === 'number' && forcedProjectId > 0 ? (
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Projet sélectionné (via URL):{' '}
                  <span className="font-medium text-slate-900 dark:text-white">
                    {forcedProjectQuery.data?.title ?? `#${forcedProjectId}`}
                  </span>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">Platform Project</label>
                  <ProjectSearchSelect
                    value={selectedProjectIri}
                    onChange={(iri) => {
                      setSelectedProjectIri(iri);
                      setSelectedBuildingId(0);
                      setSelectedApartmentId(0);
                      setSelectedRoomId(0);
                    }}
                  />
                </div>
              )}

              {(areasQuery.error || boxesQuery.error || devicesQuery.error || roomsWithRemovedDevicesQuery.error) && (
                <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                  {getErrorMessage(areasQuery.error || boxesQuery.error || devicesQuery.error || roomsWithRemovedDevicesQuery.error)}
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="border-b border-slate-200 dark:border-[#1E293B] mb-6">
              <nav className="flex space-x-1" aria-label="Tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    disabled={!platformProjectId}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      activeTab === tab.id
                        ? 'bg-primary/10 text-primary dark:text-accent border-b-2 border-primary dark:border-accent'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {!platformProjectId && (
              <div className="text-sm text-slate-500 dark:text-slate-400">Sélectionne un projet.</div>
            )}

            {platformProjectId && activeTab === 'summary' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <SimpleTable
                  title={`Appartements avec 0 box (${apartmentsZeroBox.length})`}
                  rows={apartmentsZeroBox}
                  columns={[
                    { key: 'id', label: 'ID' },
                    { key: 'name', label: 'Nom' },
                    { key: 'code', label: 'Code' },
                  ]}
                />
                <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-200 dark:border-[#1E293B] flex items-center justify-between">
                    <div className="font-semibold text-slate-900 dark:text-white">
                      {`Chambres avec 0 devices (${roomsZeroDevices.length})`}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">rows={roomsZeroDevices.length}</div>
                  </div>
                  <div className="p-5 overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 dark:bg-[#0B1116]">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                            Chambre
                          </th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {roomsZeroDevices.length === 0 ? (
                          <tr>
                            <td colSpan={2} className="px-4 py-6 text-sm text-slate-500 dark:text-slate-400">
                              —
                            </td>
                          </tr>
                        ) : (
                          roomsZeroDevices.map((r) => (
                            <tr
                              key={r.id}
                              className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
                            >
                              <td className="px-4 py-3 whitespace-nowrap">{r.name}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-right">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const area = areasById.get(r.id);
                                    if (!area) return;
                                    const buildingId = resolveAncestorIdByTypeCode(area, 'BUILDING', areasById) ?? 0;
                                    const apartmentId = resolveAncestorIdByTypeCode(area, 'APPARTEMENT', areasById) ?? 0;
                                    setActiveTab('rooms');
                                    setSelectedBuildingId(buildingId);
                                    setSelectedApartmentId(apartmentId);
                                    setSelectedRoomId(r.id);
                                  }}
                                  className="px-3 py-1.5 rounded-md bg-primary text-white text-xs font-semibold hover:bg-primary/90"
                                >
                                  Ouvrir
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <SimpleTable
                  title={`Chambres avec devices removed=1 (${roomsWithRemovedDevicesQuery.data?.count ?? 0})`}
                  rows={(roomsWithRemovedDevicesQuery.data?.rooms ?? []) as any}
                  columns={[
                    { key: 'id', label: 'ID' },
                    { key: 'name', label: 'Nom' },
                    { key: 'code', label: 'Code' },
                    { key: 'removedDevicesCount', label: 'removed' },
                  ]}
                />
              </div>
            )}

            {platformProjectId && activeTab === 'apartments' && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">Bâtiment</label>
                    <select
                      value={selectedBuildingId}
                      onChange={(e) => {
                        setSelectedBuildingId(parseInt(e.target.value, 10) || 0);
                        setSelectedApartmentId(0);
                      }}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-sm"
                    >
                      <option value={0}>— sélectionner —</option>
                      {buildings
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">Appartement</label>
                    <select
                      value={selectedApartmentId}
                      onChange={(e) => setSelectedApartmentId(parseInt(e.target.value, 10) || 0)}
                      disabled={selectedBuildingId === 0}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-sm disabled:opacity-50"
                    >
                      <option value={0}>— sélectionner —</option>
                      {apartmentsByBuilding.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {selectedApartmentId === 0 ? (
                  <div className="text-sm text-slate-500 dark:text-slate-400">Sélectionne un appartement.</div>
                ) : (
                  <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl p-5 space-y-3">
                    <div className="rounded-lg border border-slate-200 dark:border-[#1E293B] p-4">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-slate-900 dark:text-white">
                          Boxs déjà associés à cet appartement
                        </div>
                        {(associateBoxMutation.isPending || disassociateBoxMutation.isPending) && (
                          <div className="text-xs text-slate-500 dark:text-slate-400">Mise à jour...</div>
                        )}
                      </div>

                      <div className="mt-3 overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-slate-50 dark:bg-[#0B1116]">
                            <tr>
                              <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                                Box
                              </th>
                              <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                                Code
                              </th>
                              <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedApartmentBoxes.length === 0 ? (
                              <tr>
                                <td colSpan={3} className="px-3 py-3 text-sm text-slate-500 dark:text-slate-400">
                                  —
                                </td>
                              </tr>
                            ) : (
                              selectedApartmentBoxes.map((b) => (
                                <tr
                                  key={b.id}
                                  className="border-t border-slate-200/60 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
                                >
                                  <td className="px-3 py-2 whitespace-nowrap">{b.name}</td>
                                  <td className="px-3 py-2 whitespace-nowrap">{b.code || '—'}</td>
                                  <td className="px-3 py-2 whitespace-nowrap text-right">
                                    <button
                                      type="button"
                                      disabled={disassociateBoxMutation.isPending}
                                      onClick={() => disassociateBoxMutation.mutate({ boxId: b.id, areaId: selectedApartmentId })}
                                      className="px-3 py-1.5 rounded-md bg-red-600 text-white text-xs font-semibold hover:bg-red-500 disabled:opacity-50"
                                    >
                                      Retirer
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <div className="font-semibold text-slate-900 dark:text-white">Associer des boxs</div>
                      <input
                        value={boxSearch}
                        onChange={(e) => setBoxSearch(e.target.value)}
                        placeholder="Rechercher box..."
                        className="px-3 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-sm w-64"
                      />
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 dark:bg-[#0B1116]">
                          <tr>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                              Box
                            </th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                              Code
                            </th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredBoxes.map((b) => {
                            const isLinked = selectedApartmentBoxIds.has(b.id);
                            return (
                              <tr key={b.id} className="border-b border-slate-100 dark:border-slate-800">
                                <td className="px-4 py-3 whitespace-nowrap">{b.name}</td>
                                <td className="px-4 py-3 whitespace-nowrap">{b.code}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-right">
                                  {isLinked ? (
                                    <button
                                      type="button"
                                      disabled={disassociateBoxMutation.isPending}
                                      onClick={() => disassociateBoxMutation.mutate({ boxId: b.id, areaId: selectedApartmentId })}
                                      className="px-3 py-1.5 rounded-md bg-red-600 text-white text-xs font-semibold hover:bg-red-500 disabled:opacity-50"
                                    >
                                      Retirer
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      disabled={associateBoxMutation.isPending}
                                      onClick={() => associateBoxMutation.mutate({ boxId: b.id, areaId: selectedApartmentId })}
                                      className="px-3 py-1.5 rounded-md bg-primary text-white text-xs font-semibold hover:bg-primary/90 disabled:opacity-50"
                                    >
                                      Associer
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {platformProjectId && activeTab === 'apartmentDeviceCategories' && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">Bâtiment</label>
                    <select
                      value={selectedBuildingId}
                      onChange={(e) => {
                        setSelectedBuildingId(parseInt(e.target.value, 10) || 0);
                        setSelectedApartmentId(0);
                      }}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-sm"
                    >
                      <option value={0}>— sélectionner —</option>
                      {buildings
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">Appartement</label>
                    <select
                      value={selectedApartmentId}
                      onChange={(e) => setSelectedApartmentId(parseInt(e.target.value, 10) || 0)}
                      disabled={selectedBuildingId === 0}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-sm disabled:opacity-50"
                    >
                      <option value={0}>— sélectionner —</option>
                      {apartmentsByBuilding.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {selectedApartmentId === 0 ? (
                  <div className="text-sm text-slate-500 dark:text-slate-400">Sélectionne un appartement.</div>
                ) : apartmentRoomsAttributesQuery.isLoading ? (
                  <div className="text-sm text-slate-500 dark:text-slate-400">Chargement des chambres et attributs…</div>
                ) : apartmentRoomsAttributesQuery.isError ? (
                  <div className="text-sm text-red-600 dark:text-red-400">
                    Erreur lors du chargement des attributs des chambres.
                  </div>
                ) : apartmentAttributesLastQuery.isLoading ? (
                  <div className="text-sm text-slate-500 dark:text-slate-400">Chargement des dernières valeurs (Influx)…</div>
                ) : apartmentAttributesLastQuery.isError ? (
                  <div className="text-sm text-red-600 dark:text-red-400">
                    Erreur lors du chargement des dernières valeurs (Influx).
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(apartmentRoomsAttributesQuery.data?.rooms ?? []).length === 0 ? (
                      <div className="text-sm text-slate-500 dark:text-slate-400">Aucune chambre trouvée dans cet appartement.</div>
                    ) : (
                      (apartmentRoomsAttributesQuery.data?.rooms ?? []).map((room: any) => (
                        <div
                          key={room.id}
                          className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl overflow-hidden"
                        >
                          <div className="px-5 py-4 border-b border-slate-200 dark:border-[#1E293B] flex items-center justify-between">
                            <div className="font-semibold text-slate-900 dark:text-white">{room.name}</div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              attrs={(room.attributes ?? []).length}
                            </div>
                          </div>
                          <div className="p-5 overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead className="bg-slate-50 dark:bg-[#0B1116]">
                                <tr>
                                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                                    Name
                                  </th>
                                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                                    Attribute type
                                  </th>
                                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                                    Unit
                                  </th>
                                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                                    Last (Influx)
                                  </th>
                                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                                    Last time
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {(room.attributes ?? []).length === 0 ? (
                                  <tr>
                                    <td colSpan={5} className="px-4 py-6 text-sm text-slate-500 dark:text-slate-400">
                                      —
                                    </td>
                                  </tr>
                                ) : (
                                  (room.attributes ?? []).map((a: any) => (
                                    (() => {
                                      const uuid: string | null = typeof a.uuid === 'string' ? a.uuid : null;
                                      const last = uuid ? lastByUuid.get(uuid) : null;
                                      const lastValue =
                                        last && last.error === null
                                          ? last.value === null || last.value === undefined
                                            ? '—'
                                            : String(last.value)
                                          : '—';
                                      const lastTime = last && last.error === null ? (last.time ?? '—') : '—';
                                      const isStale =
                                        typeof last?.time === 'string' &&
                                        last.time.trim() !== '' &&
                                        Number.isFinite(Date.parse(last.time)) &&
                                        Date.now() - Date.parse(last.time) > 7 * 24 * 60 * 60 * 1000;

                                      return (
                                    <tr
                                      key={a.id}
                                      className={`border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors ${
                                        uuid ? 'cursor-pointer' : ''
                                      }`}
                                      onClick={() => {
                                        if (!uuid) return;
                                        setSelectedAttributeForModal({
                                          uuid,
                                          name: a.name || '—',
                                          roomName: room.name,
                                          unit: a.attributeTypeUnit ?? null,
                                          attributeTypeCode: a.attributeTypeCode ?? null,
                                        });
                                        setIsAttrModalOpen(true);
                                      }}
                                    >
                                      <td className="px-4 py-3 whitespace-nowrap">{a.name || '—'}</td>
                                      <td className="px-4 py-3 whitespace-nowrap">
                                        {a.attributeTypeName || a.attributeTypeCode || '—'}
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">{a.attributeTypeUnit || '—'}</td>
                                      <td className="px-4 py-3 whitespace-nowrap font-mono text-slate-700 dark:text-slate-200">
                                        {lastValue}
                                      </td>
                                      <td
                                        className={`px-4 py-3 whitespace-nowrap font-mono text-xs ${
                                          isStale ? 'text-rose-700 dark:text-rose-300' : 'text-slate-600 dark:text-slate-300'
                                        }`}
                                      >
                                        {lastTime}
                                      </td>
                                    </tr>
                                      );
                                    })()
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {platformProjectId && activeTab === 'rooms' && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">Bâtiment</label>
                    <select
                      value={selectedBuildingId}
                      onChange={(e) => {
                        setSelectedBuildingId(parseInt(e.target.value, 10) || 0);
                        setSelectedApartmentId(0);
                        setSelectedRoomId(0);
                      }}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-sm"
                    >
                      <option value={0}>— sélectionner —</option>
                      {buildings
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">Appartement</label>
                    <select
                      value={selectedApartmentId}
                      onChange={(e) => {
                        setSelectedApartmentId(parseInt(e.target.value, 10) || 0);
                        setSelectedRoomId(0);
                      }}
                      disabled={selectedBuildingId === 0}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-sm disabled:opacity-50"
                    >
                      <option value={0}>— sélectionner —</option>
                      {apartmentsByBuilding.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">Chambre</label>
                    <select
                      value={selectedRoomId}
                      onChange={(e) => setSelectedRoomId(parseInt(e.target.value, 10) || 0)}
                      disabled={selectedApartmentId === 0}
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-sm disabled:opacity-50"
                    >
                      <option value={0}>— sélectionner —</option>
                      {roomsByApartment.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {selectedRoomId === 0 ? (
                  <div className="text-sm text-slate-500 dark:text-slate-400">Sélectionne une chambre.</div>
                ) : (
                  <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl p-5 space-y-3">
                    <div className="rounded-lg border border-slate-200 dark:border-[#1E293B] p-4">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-semibold text-slate-900 dark:text-white">
                          Devices déjà associés à cette chambre
                        </div>
                        {roomDevicesQuery.isFetching && (
                          <div className="text-xs text-slate-500 dark:text-slate-400">Chargement...</div>
                        )}
                      </div>
                      {roomDevicesQuery.error && (
                        <div className="mt-2 text-sm text-red-700 dark:text-red-300">{getErrorMessage(roomDevicesQuery.error)}</div>
                      )}

                      {roomDevicesQuery.data && (
                        <div className="mt-3 overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead className="bg-slate-50 dark:bg-[#0B1116]">
                              <tr>
                                <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                                  Device
                                </th>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                                  Type
                                </th>
                                <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                                  Statut
                                </th>
                                <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {roomDevicesQuery.data.devices.length === 0 ? (
                                <tr>
                                  <td colSpan={4} className="px-3 py-3 text-sm text-slate-500 dark:text-slate-400">
                                    —
                                  </td>
                                </tr>
                              ) : (
                                roomDevicesQuery.data.devices.map((d) => (
                                  <tr key={d.id} className="border-t border-slate-200/60 dark:border-slate-800">
                                    <td className="px-3 py-2 whitespace-nowrap">{d.name}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">{d.type ?? '—'}</td>
                                    <td className="px-3 py-2 whitespace-nowrap">
                                      {d.removed ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-950/30 dark:text-red-300">
                                          removed=1
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300">
                                          active
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-right">
                                      <button
                                        type="button"
                                        disabled={disassociateDeviceMutation.isPending}
                                        onClick={() =>
                                          disassociateDeviceMutation.mutate({ deviceId: d.id, areaId: selectedRoomId })
                                        }
                                        className="px-3 py-1.5 rounded-md bg-red-600 text-white text-xs font-semibold hover:bg-red-500 disabled:opacity-50"
                                      >
                                        Dissocier
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <div className="font-semibold text-slate-900 dark:text-white">Associer des devices (removed=0 uniquement)</div>
                      <input
                        value={deviceSearch}
                        onChange={(e) => setDeviceSearch(e.target.value)}
                        placeholder="Rechercher device..."
                        className="px-3 py-2 rounded-lg border border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#0B1116] text-sm w-64"
                      />
                    </div>

                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 dark:bg-[#0B1116]">
                          <tr>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                              Device
                            </th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                              Type
                            </th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredDevices.map((d) => {
                            const isLinked = d.areaIds.includes(selectedRoomId);
                            return (
                              <tr
                                key={d.id}
                                className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
                              >
                                <td className="px-4 py-3 whitespace-nowrap">{d.name}</td>
                                <td className="px-4 py-3 whitespace-nowrap">{d.type ?? '—'}</td>
                                <td className="px-4 py-3 whitespace-nowrap text-right">
                                  {isLinked ? (
                                    <button
                                      type="button"
                                      disabled={disassociateDeviceMutation.isPending}
                                      onClick={() => disassociateDeviceMutation.mutate({ deviceId: d.id, areaId: selectedRoomId })}
                                      className="px-3 py-1.5 rounded-md bg-red-600 text-white text-xs font-semibold hover:bg-red-500 disabled:opacity-50"
                                    >
                                      Retirer
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      disabled={associateDeviceMutation.isPending}
                                      onClick={() => associateDeviceMutation.mutate({ deviceId: d.id, areaId: selectedRoomId })}
                                      className="px-3 py-1.5 rounded-md bg-primary text-white text-xs font-semibold hover:bg-primary/90 disabled:opacity-50"
                                    >
                                      Associer
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      <AttributeTimeseriesModal
        isOpen={isAttrModalOpen}
        onClose={() => {
          setIsAttrModalOpen(false);
          setSelectedAttributeForModal(null);
        }}
        attribute={selectedAttributeForModal}
      />
    </div>
  );
}

type Column<T extends Record<string, unknown>> = { key: keyof T; label: string };

function SimpleTable<T extends Record<string, unknown>>({
  title,
  rows,
  columns,
}: {
  title: string;
  rows: T[];
  columns: Column<T>[];
}) {
  return (
    <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-200 dark:border-[#1E293B] flex items-center justify-between">
        <div className="font-semibold text-slate-900 dark:text-white">{title}</div>
        <div className="text-xs text-slate-500 dark:text-slate-400">rows={rows.length}</div>
      </div>
      <div className="p-5 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 dark:bg-[#0B1116]">
            <tr>
              {columns.map((c) => (
                <th
                  key={String(c.key)}
                  className="text-left px-4 py-3 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider border-b border-slate-200 dark:border-[#1E293B]"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-6 text-sm text-slate-500 dark:text-slate-400">
                  —
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr key={idx} className="border-b border-slate-100 dark:border-slate-800">
                  {columns.map((c) => (
                    <td key={String(c.key)} className="px-4 py-3 whitespace-nowrap">
                      {r[c.key] === null || r[c.key] === undefined ? '—' : String(r[c.key])}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

